import {noop} from '@benchling/js-utils';
import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React, {useMemo} from 'react';

import * as analytics from 'analytics/Analytics';
import {assert} from 'Asserts';
import {ActionMethod, ActionOrigin, WORKFLOWS_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {ProcessFlowchartNodeDetailsType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  FlowchartEdge,
  ProcessFlowchartEdgeType,
  TransitionSource,
  TaskSchemaNodeSection,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';
import {
  TaskSchemaNode,
  FlowchartNode,
  isInteractiveTaskNode,
  getInteractiveTaskNodeDatas,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorNodeTypes';
import {getTransitionSourceByNodeIdPairKey} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/TransitionSourceUtils';
import {getTransitionSourceForNodePairWithDefault} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/types/transition/transitionLookupUtils';
import {useTransitionsContext} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/types/transition/TransitionsContext';
import {getExecutionNodeTypeIdentifierForNode} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/useFlowchartState/useFlowchartStateAnalytics';
import Chip, {ChipSize} from 'ui-library/chips/Chip';
import {ORANGE_DARK} from 'ui-library/Colors';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';

import {findAllDownstreamMappableNodeIds, findAllUpstreamMappableNodeIds} from '../TraverseFlowchart';
import CollapsibleTransitionCard from './CollapsibleTransitionCard';
import {NodeDetailsSidepanelContext} from './NodeDetailsSidepanelContext';
import {TransitionMappingsTable} from './TransitionFieldInfoSection';

function getOptionTextForTransitionSource(transitionSource: TransitionSource): string {
  switch (transitionSource) {
    case TransitionSource.OUTPUTS:
      return 'outputs';
    case TransitionSource.INPUTS:
      return 'tasks';
    default:
      throw new Error(`Unexpected transition source: ${transitionSource}`);
  }
}

export interface FlowchartSettingsTabContentProps {
  locked?: boolean;
}

enum TransitionDirection {
  INBOUND = 'inbound',
  OUTBOUND = 'outbound',
}

interface TransitionSourceDescriptionProps {
  isRootSourceNode: boolean;
  isTargetOutputNode: boolean;
  transitionSource: TransitionSource;
  availableTransitionSources: Array<TransitionSource>;
  connectedNodeId: string;
  connectedNodeName: string;
  handleTransitionSourceSelect: (nodeId: string, value: TransitionSource) => void;
  locked?: boolean;
}

/**
 * Renders the description for a transition source, including a dropdown to select between
 * available sources (e.g. Outputs vs Tasks). For root source nodes, displays a static label
 * instead. Shows a warning when the Tasks source is selected, since it limits field mappings
 * to input fields from the previous step.
 */
function TransitionSourceDescription(props: TransitionSourceDescriptionProps): JSX.Element {
  const {
    isRootSourceNode,
    isTargetOutputNode,
    transitionSource,
    availableTransitionSources,
    connectedNodeId,
    connectedNodeName,
    handleTransitionSourceSelect,
    locked,
  } = props;

  if (isRootSourceNode) {
    return (
      <div className={css(styles.cardBody)}>
        <span>
          Uses <strong>tasks</strong> to create tasks &amp; populate fields
        </span>
      </div>
    );
  }

  return (
    <div className={css(styles.cardBody)}>
      <span>Use&nbsp;</span>
      <div className={css(styles.fieldSourceDropdownWrapper)}>
        <SelectDropdown
          dataTestAttrs={{
            component: 'TransitionSourceDropdown',
            element: 'root',
            key: connectedNodeName,
          }}
          menu={
            <Menu height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.LARGE} value={transitionSource}>
              {availableTransitionSources.map((source) => (
                <Option
                  key={source}
                  value={source}
                  onSelect={(value: TransitionSource) => {
                    handleTransitionSourceSelect(connectedNodeId, value);
                  }}
                  isDisabled={locked}
                >
                  {getOptionTextForTransitionSource(source)}
                </Option>
              ))}
            </Menu>
          }
          displayName={getOptionTextForTransitionSource(transitionSource)}
        />
      </div>
      {isTargetOutputNode ? (
        <span>&nbsp;to populate output fields on this flowchart</span>
      ) : (
        <span>&nbsp;to create tasks &amp; populate fields</span>
      )}
      {transitionSource === TransitionSource.INPUTS && (
        <div className={css(styles.warningMessage)}>
          <BenchlingIcon icon="warning" className={css(styles.warningIcon)} />
          <span className={css(styles.secondaryText)}>
            Limits field mappings to input fields from the previous step
          </span>
        </div>
      )}
    </div>
  );
}

interface TransitionCardFieldMappingsProps {
  selectedNode: TaskSchemaNode;
  edges: Array<FlowchartEdge>;
  locked?: boolean;
}

/**
 * Renders the field mapping tables for a transition's data flow edges within a collapsible
 * card. Shows an empty state message when no edges exist.
 */
function TransitionCardFieldMappings(props: TransitionCardFieldMappingsProps): JSX.Element {
  const {selectedNode, edges, locked} = props;
  const context = NodeDetailsSidepanelContext.useContext();

  if (edges.length === 0) {
    return <div className={css(styles.emptyMappingsText)}>No field mappings configured yet.</div>;
  }

  return (
    <TransitionMappingsTable
      selectedFieldId=""
      node={selectedNode}
      flowchart={context.interactiveFlowchart}
      flowchartTaskSchema={context.flowchartTaskSchema}
      allSchemasById={context.allSchemasById}
      onChangeCustomMappingData={context.onChangeCustomMappingData}
      onChangeSelectedFieldId={noop}
      locked={locked}
      validationErrors={[]}
      edges={edges}
      tableDescription=""
      showBorder={false}
      className={css(styles.fieldTable)}
    />
  );
}

interface TransitionNodeContentProps {
  direction: TransitionDirection;
  connectedNode: TaskSchemaNode;
  transitionSourceNode: TaskSchemaNode;
  sidepanelSelectedNode: TaskSchemaNode;
  selectedNodeIsOutputNode: boolean;
  transitionSource: TransitionSource;
  handleTransitionSourceSelect: (nodeId: string, value: TransitionSource) => void;
  edges: Array<FlowchartEdge>;
  locked?: boolean;
  isPartOfMergingTransition: boolean;
}

const DIRECTION_CONFIG: Record<
  TransitionDirection,
  {title: string; emptyMessage: string; preposition: string}
> = {
  [TransitionDirection.INBOUND]: {
    title: 'Inbound transitions',
    emptyMessage: 'Draw mappings to this task schema to configure them here.',
    preposition: 'into',
  },
  [TransitionDirection.OUTBOUND]: {
    title: 'Outbound transitions',
    emptyMessage: 'Draw mappings from this task schema to configure them here.',
    preposition: 'from',
  },
};

/**
 * Derives display properties for a TransitionSourceDescription based on the node pair's
 * direction and types.
 */
function getTransitionSourceDescriptionProps(
  direction: TransitionDirection,
  connectedNode: TaskSchemaNode,
  transitionSourceNode: TaskSchemaNode,
  selectedNodeIsOutputNode: boolean
): Pick<
  TransitionSourceDescriptionProps,
  'isRootSourceNode' | 'isTargetOutputNode' | 'availableTransitionSources'
> {
  const isRootSourceNode =
    direction === TransitionDirection.INBOUND &&
    connectedNode.data.nodeDetailsType === ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS;

  const isTargetOutputNode =
    direction === TransitionDirection.INBOUND
      ? selectedNodeIsOutputNode
      : connectedNode.data.nodeDetailsType === ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS;

  const hasOutputSection = transitionSourceNode.data.sections.some(
    (section) => section.sectionName === TaskSchemaNodeSection.OUTPUTS
  );
  const availableTransitionSources = hasOutputSection
    ? Object.values(TransitionSource)
    : [TransitionSource.INPUTS];

  return {isRootSourceNode, isTargetOutputNode, availableTransitionSources};
}

/**
 * Per-node content for a transition, including a node chip to show which connected node we're configuring,
 * a transition source description allowing us to choose a transition source,
 * and field mapping tables for configuration of the transition's data flow edges.
 */
function TransitionNodeContent(props: TransitionNodeContentProps): JSX.Element {
  const {
    direction,
    connectedNode,
    transitionSourceNode,
    sidepanelSelectedNode,
    selectedNodeIsOutputNode,
    transitionSource,
    handleTransitionSourceSelect,
    edges,
    locked,
    isPartOfMergingTransition,
  } = props;

  const {isRootSourceNode, isTargetOutputNode, availableTransitionSources} =
    getTransitionSourceDescriptionProps(
      direction,
      connectedNode,
      transitionSourceNode,
      selectedNodeIsOutputNode
    );

  return (
    // Merging transitions have a couple of extra components (each node gets its own section inside the card body)
    <div className={isPartOfMergingTransition ? css(styles.mergingNodeBlock) : undefined}>
      {isPartOfMergingTransition && (
        <div className={css(styles.mergingNodeBlockHeader)}>
          <Chip
            className={css(styles.chipNormalWeight)}
            text={connectedNode.data.nodeName}
            icon={connectedNode.data.nodeIcon}
            size={ChipSize.MINI}
          />
        </div>
      )}
      {isPartOfMergingTransition && (
        <TransitionSourceDescription
          isRootSourceNode={isRootSourceNode}
          isTargetOutputNode={isTargetOutputNode}
          transitionSource={transitionSource}
          availableTransitionSources={availableTransitionSources}
          connectedNodeId={connectedNode.id}
          connectedNodeName={connectedNode.data.nodeName}
          handleTransitionSourceSelect={handleTransitionSourceSelect}
          locked={locked}
        />
      )}
      <TransitionCardFieldMappings selectedNode={sidepanelSelectedNode} edges={edges} locked={locked} />
    </div>
  );
}

interface TransitionSettingsContentProps {
  direction: TransitionDirection;
  selectedNode: TaskSchemaNode;
  selectedNodeIsOutputNode: boolean;
  connectedNodes: Array<TaskSchemaNode>;
  transitionSources: Record<string, TransitionSource>;
  handleTransitionSourceSelect: (nodeIdToUpdate: string, value: TransitionSource) => void;
  locked?: boolean;
}

/**
 * Renders the settings for transitions in a given direction (inbound or outbound).
 * Shows a section title, description, and a collapsible card containing transition source
 * selectors and field mapping tables for each connected node. For single-node transitions,
 * the source description is shown in the card's always-visible area; for merging transitions
 * each node gets its own section inside the card body.
 */
function TransitionSettingsContent(props: TransitionSettingsContentProps): JSX.Element {
  const {
    direction,
    selectedNode,
    selectedNodeIsOutputNode,
    connectedNodes,
    transitionSources,
    handleTransitionSourceSelect,
    locked,
  } = props;
  const {interactiveFlowchart} = NodeDetailsSidepanelContext.useContext();

  const {title, emptyMessage, preposition} = DIRECTION_CONFIG[direction];

  if (connectedNodes.length === 0) {
    return (
      <div>
        <div className={css(styles.sectionTitle)}>{title}</div>
        <div className={css(styles.emptyDirectionMessage)}>{emptyMessage}</div>
      </div>
    );
  }

  const isMerging = connectedNodes.length > 1;

  function getDataFlowEdgesForConnectedNode(connectedNodeId: string): Array<FlowchartEdge> {
    const [sourceId, targetId] =
      direction === TransitionDirection.INBOUND
        ? [connectedNodeId, selectedNode.id]
        : [selectedNode.id, connectedNodeId];
    return interactiveFlowchart.edges.filter(
      (edge) =>
        edge.type === ProcessFlowchartEdgeType.DATA_FLOW &&
        edge.source === sourceId &&
        edge.target === targetId
    );
  }

  function getTransitionSourceNode(connectedNode: TaskSchemaNode): TaskSchemaNode {
    return direction === TransitionDirection.INBOUND ? connectedNode : selectedNode;
  }

  const singleNodeSourceDescription = !isMerging
    ? (() => {
        const node = connectedNodes[0];
        const {isRootSourceNode, isTargetOutputNode, availableTransitionSources} =
          getTransitionSourceDescriptionProps(
            direction,
            node,
            getTransitionSourceNode(node),
            selectedNodeIsOutputNode
          );
        return (
          <TransitionSourceDescription
            isRootSourceNode={isRootSourceNode}
            isTargetOutputNode={isTargetOutputNode}
            transitionSource={transitionSources[node.id]}
            availableTransitionSources={availableTransitionSources}
            connectedNodeId={node.id}
            connectedNodeName={node.data.nodeName}
            handleTransitionSourceSelect={handleTransitionSourceSelect}
            locked={locked}
          />
        );
      })()
    : undefined;

  return (
    <div>
      <div className={css(styles.sectionTitle)}>{title}</div>
      <div className={css(styles.description)}>
        <span>Configure behavior and mappings for transitions {preposition}&nbsp;</span>
        <Chip
          className={css(styles.chipNormalWeight)}
          text={selectedNode.data.nodeName}
          icon={selectedNode.data.nodeIcon}
          size={ChipSize.MINI}
        />
      </div>
      <CollapsibleTransitionCard
        header={direction === TransitionDirection.INBOUND ? 'From' : 'To'}
        headerAdornment={
          <>
            {connectedNodes.map((node) => (
              <Chip
                className={css(styles.chipNormalWeight)}
                key={node.id}
                text={node.data.nodeName}
                icon={node.data.nodeIcon}
                size={ChipSize.MINI}
              />
            ))}
          </>
        }
        alwaysVisibleContent={singleNodeSourceDescription}
        fullWidthChildren={true}
      >
        {connectedNodes.map((connectedNode) => {
          const edgesForPair = getDataFlowEdgesForConnectedNode(connectedNode.id);

          return (
            <TransitionNodeContent
              key={connectedNode.id}
              direction={direction}
              connectedNode={connectedNode}
              transitionSourceNode={getTransitionSourceNode(connectedNode)}
              sidepanelSelectedNode={selectedNode}
              selectedNodeIsOutputNode={selectedNodeIsOutputNode}
              transitionSource={transitionSources[connectedNode.id]}
              handleTransitionSourceSelect={handleTransitionSourceSelect}
              edges={edgesForPair}
              locked={locked}
              isPartOfMergingTransition={isMerging}
            />
          );
        })}
      </CollapsibleTransitionCard>
    </div>
  );
}

function areAllNodesInteractiveTaskNodes(nodes: Array<FlowchartNode>): nodes is Array<TaskSchemaNode> {
  return nodes.every((node) => isInteractiveTaskNode(node));
}

export default function FlowchartSettingsPane(props: FlowchartSettingsTabContentProps): JSX.Element | null {
  const {selectedNode, interactiveFlowchart, onChangeTransitionSource, flowchartEditorConfigurator} =
    NodeDetailsSidepanelContext.useContext();
  const {transitionsById, ptdvPairToTransitionIdIndex} = useTransitionsContext();

  const {upstreamMappableNodeIds, inboundTransitionSources} = useMemo(() => {
    const upstreamMappableNodeIds = findAllUpstreamMappableNodeIds(interactiveFlowchart, selectedNode.id);
    const taskNodes = getInteractiveTaskNodeDatas(interactiveFlowchart.nodes);
    const inboundTransitionSources: Record<string, TransitionSource> = {};
    for (const sourceNodeId of upstreamMappableNodeIds) {
      inboundTransitionSources[sourceNodeId] = getTransitionSourceForNodePairWithDefault({
        sourceNodeId,
        targetNodeId: selectedNode.id,
        nodes: taskNodes,
        transitionsById,
        ptdvPairToTransitionIdIndex,
        nodesForDefault: taskNodes,
      });
    }
    return {upstreamMappableNodeIds, inboundTransitionSources};
  }, [interactiveFlowchart, selectedNode.id, transitionsById, ptdvPairToTransitionIdIndex]);

  const {downstreamMappableNodeIds, outboundTransitionSources} = useMemo(() => {
    const downstreamMappableNodeIds = findAllDownstreamMappableNodeIds(interactiveFlowchart, selectedNode.id);
    const taskNodes = getInteractiveTaskNodeDatas(interactiveFlowchart.nodes);
    const outboundTransitionSources: Record<string, TransitionSource> = {};
    for (const targetNodeId of downstreamMappableNodeIds) {
      outboundTransitionSources[targetNodeId] = getTransitionSourceForNodePairWithDefault({
        sourceNodeId: selectedNode.id,
        targetNodeId,
        nodes: taskNodes,
        transitionsById,
        ptdvPairToTransitionIdIndex,
        nodesForDefault: taskNodes,
      });
    }
    return {downstreamMappableNodeIds, outboundTransitionSources};
  }, [interactiveFlowchart, selectedNode.id, transitionsById, ptdvPairToTransitionIdIndex]);

  const {nodes} = interactiveFlowchart;
  const inboundConnectedNodes = Object.values(nodes).filter((node) =>
    upstreamMappableNodeIds.includes(node.id)
  );
  assert(
    areAllNodesInteractiveTaskNodes(inboundConnectedNodes),
    'Expected all inbound connected nodes to be task nodes'
  );

  const outboundConnectedNodes = Object.values(nodes).filter((node) =>
    downstreamMappableNodeIds.includes(node.id)
  );
  assert(
    areAllNodesInteractiveTaskNodes(outboundConnectedNodes),
    'Expected all outbound connected nodes to be task nodes'
  );

  if (!isInteractiveTaskNode(selectedNode)) {
    return null;
  }

  const selectedNodeIsOutputNode =
    selectedNode.data.nodeDetailsType === ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS;

  const handleTransitionSourceSelect = (nodeIdToUpdate: string, value: TransitionSource): void => {
    const isInbound = upstreamMappableNodeIds.includes(nodeIdToUpdate);
    const currentTransitionSource = isInbound
      ? inboundTransitionSources[nodeIdToUpdate]
      : outboundTransitionSources[nodeIdToUpdate];
    const nodeIdPair = isInbound
      ? getTransitionSourceByNodeIdPairKey(nodeIdToUpdate, selectedNode.id)
      : getTransitionSourceByNodeIdPairKey(selectedNode.id, nodeIdToUpdate);

    if (currentTransitionSource !== value) {
      analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.TRANSITION_SOURCE_CHANGED, {
        oldValue: currentTransitionSource,
        newValue: value,
        flowchartType: flowchartEditorConfigurator.flowchartTypeName,
        actionMethod: ActionMethod.BUTTON_CLICK,
        actionOrigin: ActionOrigin.WORKFLOWS_FLOWCHART_FIRST_CONFIGURATION_CANVAS,
        nodeType: getExecutionNodeTypeIdentifierForNode(selectedNode),
      });
      onChangeTransitionSource(nodeIdPair, value);
    }
  };

  const hasOutbound = outboundConnectedNodes.length > 0;
  const showOutbound = hasOutbound || !selectedNodeIsOutputNode;

  return (
    <div key={selectedNode.id}>
      <TransitionSettingsContent
        direction={TransitionDirection.INBOUND}
        selectedNode={selectedNode}
        selectedNodeIsOutputNode={selectedNodeIsOutputNode}
        connectedNodes={inboundConnectedNodes}
        transitionSources={inboundTransitionSources}
        handleTransitionSourceSelect={handleTransitionSourceSelect}
        locked={props.locked}
      />
      {showOutbound && <div className={css(styles.sectionDivider)} />}
      {showOutbound && (
        <TransitionSettingsContent
          direction={TransitionDirection.OUTBOUND}
          selectedNode={selectedNode}
          selectedNodeIsOutputNode={selectedNodeIsOutputNode}
          connectedNodes={outboundConnectedNodes}
          transitionSources={outboundTransitionSources}
          handleTransitionSourceSelect={handleTransitionSourceSelect}
          locked={props.locked}
        />
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  sectionDivider: {
    height: 24,
  },
  sectionTitle: {
    fontSize: '16px',
    fontWeight: 600,
    color: Color.GRAY_900,
    marginBottom: 8,
  },
  description: {
    fontSize: '14px',
    fontWeight: 400,
    color: Color.GRAY_900,
    flexWrap: 'nowrap',
    marginBottom: 12,
  },
  cardBody: {
    fontSize: 14,
    color: Color.GRAY_900,
    paddingLeft: 16,
    paddingBottom: 12,
  },
  mergingNodeBlock: {
    borderTop: `1px solid ${Color.GRAY_200}`,
    paddingTop: 12,
  },
  mergingNodeBlockHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 12,
    paddingLeft: 16,
    paddingRight: 16,
  },
  fieldTable: {
    marginBottom: 0,
    borderTop: `1px solid ${Color.GRAY_200}`,
    borderRadius: 0,
  },
  emptyDirectionMessage: {
    fontSize: 14,
    color: Color.GRAY_600,
    marginTop: 4,
  },
  emptyMappingsText: {
    fontSize: 14,
    color: Color.GRAY_600,
    paddingLeft: 16,
    paddingRight: 16,
    paddingBottom: 12,
  },
  warningMessage: {
    marginTop: '4px',
  },
  secondaryText: {
    color: Color.GRAY_600,
  },
  warningIcon: {
    paddingRight: 4,
    color: ORANGE_DARK,
  },
  fieldSourceDropdownWrapper: {
    display: 'inline-block',
  },
  chipNormalWeight: {
    fontWeight: 'normal' as const,
  },
});
